import { Button } from 'primereact/button'
import { FC, useEffect, useState, useMemo } from 'react'
import Modal from './Modal'
import CloseButton from './buttons/CloseButton'
import { classNames } from 'primereact/utils'
import { Controller, useForm } from 'react-hook-form'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { NASCARTeams, NASCAR28Teams } from '../utils/constants'

const baseAttitudeStateFields = [
  {
    name: 'name',
    label: 'State Name',
    type: 'text'
  },
  {
    name: 'front_height',
    label: 'FRH'
  },
  {
    name: 'rear_height',
    label: 'RRH'
  },
  {
    name: 'steer',
    label: 'Steer'
  },
  {
    name: 'roll',
    label: 'Roll'
  },
  {
    name: 'front_flap',
    label: 'Front Flap'
  },
  {
    name: 'rear_flap',
    label: 'Rear Flap'
  },
  {
    name: 'yaw',
    label: 'Yaw'
  },
  {
    name: 'radius',
    label: 'Rad'
  },
  {
    name: 'air_speed',
    label: 'Spd'
  },
  {
    name: 'exhaust',
    label: 'Exh'
  },
  {
    name: 'spec',
    label: 'SPEC',
    nascar28Only: true
  },
  {
    name: 'rad_blocker',
    label: 'RAD_BLOCKER',
    nascar28Only: true
  }
]
const title = 'Edit State'

type AttitudeStateEditorModalProps = {
  states: any[]
  stateIndex: number
  currentTeam?: string
  onConfirm: (formData: any) => void
  onCancel: () => void
}

const AttitudeStateEditorModal: FC<AttitudeStateEditorModalProps> = ({
  states,
  stateIndex,
  currentTeam,
  onConfirm,
  onCancel
}) => {
  const {
    control,
    setValue,
    setError,
    handleSubmit,
    formState: { errors }
  } = useForm<any>()
  const [selectedAttitudeState] = useState<any>({ ...states[stateIndex] } as any)

  // Filter attitude state fields based on team - hide Front Flap and Rear Flap for testing teams and NASCAR-Cup
  // Also hide SPEC and RAD_BLOCKER for non-NASCAR-Cup-28 teams
  const attitudeStateFields = useMemo(() => {
    const shouldHideFlapColumns =
      NASCARTeams.includes(currentTeam) ||
      currentTeam?.toLowerCase().includes('testing') ||
      currentTeam?.toLowerCase().includes('test')

    const isNascar28 = NASCAR28Teams.includes(currentTeam)

    let filtered = baseAttitudeStateFields

    if (shouldHideFlapColumns) {
      filtered = filtered.filter(
        field => field.label !== 'Front Flap' && field.label !== 'Rear Flap'
      )
    }

    if (!isNascar28) {
      filtered = filtered.filter(field => !field.nascar28Only)
    }

    return filtered
  }, [currentTeam])

  const handleSave = async formData => {
    console.log('formData', formData)
    onConfirm(formData)
  }

  const handleConfirm = () => {
    onCancel && onCancel()
  }

  useEffect(() => {
    setValue('name', selectedAttitudeState.name)
    attitudeStateFields.forEach(fieldItem => {
      // @ts-ignore
      setValue(fieldItem.name, selectedAttitudeState[fieldItem.name])
    })
  }, [])

  return (
    <Modal
      isOpen={true}
      withAction={false}
      easyClose={true}
      onCancel={() => onCancel && onCancel()}
      title={title}
      showCancelButton={true}
      zIndex={30}
      onConfirm={handleConfirm}>
      <div className="relative">
        <form
          onSubmit={handleSubmit(handleSave)}
          className="w-full grid grid-cols-8 gap-x-2 gap-y-4 mt-8">
          {attitudeStateFields.map(fieldItem => {
            const isTextField = fieldItem.type === 'text'
            return (
              <div
                className={classNames('field-box', { 'col-span-8': isTextField })}
                key={fieldItem.name}>
                <Controller
                  // @ts-ignore
                  name={fieldItem.name}
                  control={control}
                  rules={{ required: `${fieldItem.label} is required.` }}
                  render={({ field, fieldState }) => (
                    <>
                      <label htmlFor={field.name} className="mb-2">
                        {fieldItem.label} <span className="text-red-500">*</span>
                      </label>
                      {isTextField ? (
                        <InputText
                          id={field.name}
                          value={field.value || ''}
                          className={classNames('w-full', { error: fieldState.error })}
                          onChange={e => field.onChange(e.target.value)}
                        />
                      ) : (
                        <InputNumber
                          id={field.name}
                          mode="decimal"
                          maxFractionDigits={6}
                          // @ts-ignore
                          value={field.value}
                          inputClassName={classNames('w-full', { error: fieldState.error })}
                          onChange={e => field.onChange(e.value)}
                        />
                      )}
                    </>
                  )}
                />
              </div>
            )
          })}

          <div className="w-full col-span-8 pt-6 flex items-center justify-end space-x-6">
            <Button type="submit" label="Save State" />
            <CloseButton label="Close" onClick={() => onCancel && onCancel()} />
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default AttitudeStateEditorModal
